import { type FAMI } from "@/models/fami.tsx";
import { type DropDownModel } from "@/models.ts";
import { useEffect, useRef, useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { validateEffectiveStartDateForProgramYear } from "@/utils/dates";

type UseFAMIFormLogicArgs = {
  form: UseFormReturn<FAMI, any, FAMI>;
  family: FAMI;
  resetKey: string;
  isEdit: boolean;
  reasonForServiceList: DropDownModel[];
  calWORKsList: DropDownModel[];
};

const isHomelessnessCode = (value: unknown) => {
  const v = String(value ?? "").trim();
  return v === "03";
};

const findCodeByDescription = (
  options: DropDownModel[],
  description: string
): string | undefined => {
  const target = description.trim().toLowerCase();
  return options.find((o) => o.Text?.trim().toLowerCase() === target)?.Value;
};

const CALWORKS_PROGRAM_ACTIVITY_DESCRIPTION = "CalWORKs Program Activity";
const CALWORKS_CASH_AID_DESCRIPTION = "Adult(s) and Child(ren) Cash Aid";
const CALWORKS_DIVERSION_SERVICES_DESCRIPTION = "Diversion Services";

export const CALWORKS_RECIPIENT_INVALID_MESSAGE =
  "CALWorks Recipient invalid when Reasons for Needing for Service contains CALWorks Program Activity";

type HohPrefix = "HeadOfHousehold1" | "HeadOfHousehold2";

const serviceReasonFieldNames = (prefix: HohPrefix): (keyof FAMI)[] =>
  Array.from(
    { length: 9 },
    (_, i) => `${prefix}NeedForService${i + 1}` as keyof FAMI
  );

export type FamilyFicnOption = {
  ficn: string;
  headOfHousehold1: string;
  headOfHousehold2: string;
  programYear: string;
};

export function getMostRecentFamilyForFicn(
  records: FAMI[],
  ficn: string
): FAMI | undefined {
  const forFicn = (records ?? []).filter((r) => r.LocalFamilyId === ficn);
  if (forFicn.length === 0) return undefined;
  return [...forFicn].sort((a, b) => {
    const da = new Date(a.EffectiveStartDate ?? "").getTime();
    const db = new Date(b.EffectiveStartDate ?? "").getTime();
    return (
      (Number.isNaN(db) ? -Infinity : db) - (Number.isNaN(da) ? -Infinity : da)
    );
  })[0];
}

export function getFamilyFicnOptions(records: FAMI[]): FamilyFicnOption[] {
  const seen = new Map<string, FamilyFicnOption>();
  for (const r of records ?? []) {
    const ficn = r.LocalFamilyId ?? "";
    if (!ficn || seen.has(ficn)) continue;
    const mostRecent = getMostRecentFamilyForFicn(records, ficn) ?? r;
    seen.set(ficn, {
      ficn,
      headOfHousehold1: [
        mostRecent.HeadOfHousehold1FirstName,
        mostRecent.HeadOfHousehold1LastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim(),
      headOfHousehold2: [
        mostRecent.HeadOfHousehold2FirstName,
        mostRecent.HeadOfHousehold2LastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim(),
      programYear: mostRecent.ProgramYear ?? "",
    });
  }
  return Array.from(seen.values());
}

export function buildFamilyAddDefaults(record: FAMI): Partial<FAMI> {
  return {
    ReportingLEA: record.ReportingLEA,
    PreschoolCode: record.PreschoolCode,
    ProgramYear: record.ProgramYear,
    LocalFamilyId: record.LocalFamilyId,
    CSPPID: record.CSPPID,
    LocalChildId: record.LocalChildId,
    HeadOfHousehold1LastName: record.HeadOfHousehold1LastName,
    HeadOfHousehold1FirstName: record.HeadOfHousehold1FirstName,
    HeadOfHousehold1MiddleName: record.HeadOfHousehold1MiddleName,
    HeadOfHousehold1HighestEducationLevel:
      record.HeadOfHousehold1HighestEducationLevel,
    HeadOfHousehold2LastName: record.HeadOfHousehold2LastName,
    HeadOfHousehold2FirstName: record.HeadOfHousehold2FirstName,
    HeadOfHousehold2MiddleName: record.HeadOfHousehold2MiddleName,
    HeadOfHousehold2HighestEducationLevel:
      record.HeadOfHousehold2HighestEducationLevel,
    ResidentialAddress1: record.ResidentialAddress1,
    ResidentialAddress2: record.ResidentialAddress2,
    ResidentialCity: record.ResidentialCity,
    ResidentialState: record.ResidentialState,
    ResidentialZip: record.ResidentialZip,
    MailingAddress1: record.MailingAddress1,
    MailingAddress2: record.MailingAddress2,
    MailingCity: record.MailingCity,
    MailingState: record.MailingState,
    MailingZip: record.MailingZip,
    HeadOfHouseholdWrittenCommunicationPreference:
      record.HeadOfHouseholdWrittenCommunicationPreference,
    HeadOfHouseholdVerbalCommunicationPreference:
      record.HeadOfHouseholdVerbalCommunicationPreference,
    FamilyIncome: record.FamilyIncome,
    HeadOfHousehold1NeedForService1: record.HeadOfHousehold1NeedForService1,
    HeadOfHousehold1NeedForService2: record.HeadOfHousehold1NeedForService2,
    HeadOfHousehold1NeedForService3: record.HeadOfHousehold1NeedForService3,
    HeadOfHousehold1NeedForService4: record.HeadOfHousehold1NeedForService4,
    HeadOfHousehold1NeedForService5: record.HeadOfHousehold1NeedForService5,
    HeadOfHousehold1NeedForService6: record.HeadOfHousehold1NeedForService6,
    HeadOfHousehold1NeedForService7: record.HeadOfHousehold1NeedForService7,
    HeadOfHousehold1NeedForService8: record.HeadOfHousehold1NeedForService8,
    HeadOfHousehold1NeedForService9: record.HeadOfHousehold1NeedForService9,
    HeadOfHousehold2NeedForService1: record.HeadOfHousehold2NeedForService1,
    HeadOfHousehold2NeedForService2: record.HeadOfHousehold2NeedForService2,
    HeadOfHousehold2NeedForService3: record.HeadOfHousehold2NeedForService3,
    HeadOfHousehold2NeedForService4: record.HeadOfHousehold2NeedForService4,
    HeadOfHousehold2NeedForService5: record.HeadOfHousehold2NeedForService5,
    HeadOfHousehold2NeedForService6: record.HeadOfHousehold2NeedForService6,
    HeadOfHousehold2NeedForService7: record.HeadOfHousehold2NeedForService7,
    HeadOfHousehold2NeedForService8: record.HeadOfHousehold2NeedForService8,
    HeadOfHousehold2NeedForService9: record.HeadOfHousehold2NeedForService9,
    CALWorksRecipient: record.CALWorksRecipient,
    FamilySize: record.FamilySize,
    Id: "",
    EffectiveStartDate: "",
    EffectiveEndDate: "",
  };
}

export function useFAMIFormLogic({
  form,
  family,
  resetKey,
  reasonForServiceList,
  calWORKsList,
}: UseFAMIFormLogicArgs) {
  const { reset, setValue, getValues, clearErrors, trigger, watch } = form;

  const [showMailingAddress, setShowMailingAddress] = useState(false);

  const lastResetKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastResetKeyRef.current === resetKey) return;
    lastResetKeyRef.current = resetKey;

    reset(family);
    clearErrors();

    const shouldShowMailing = Boolean(
      family.MailingAddress1 ||
      family.MailingAddress2 ||
      family.MailingCity ||
      family.MailingState ||
      family.MailingZip
    );

    setShowMailingAddress(shouldShowMailing);
  }, [resetKey, reset, clearErrors, family]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "ProgramYear" && value.EffectiveStartDate) {
        trigger("EffectiveStartDate");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  const hoh1ServiceReasons = useWatch({
    control: form.control,
    name: serviceReasonFieldNames("HeadOfHousehold1") as any,
  });

  const hoh2ServiceReasons = useWatch({
    control: form.control,
    name: serviceReasonFieldNames("HeadOfHousehold2") as any,
  });

  const hoh2FirstName = useWatch({
    control: form.control,
    name: "HeadOfHousehold2FirstName",
  });

  const hoh2LastName = useWatch({
    control: form.control,
    name: "HeadOfHousehold2LastName",
  });

  const mailingAddress1 = useWatch({
    control: form.control,
    name: "MailingAddress1",
  });

  const hasHoh2FirstName = Boolean(hoh2FirstName?.trim());
  const hasHoh2LastName = Boolean(hoh2LastName?.trim());
  const hasAnyHoh2Name = hasHoh2FirstName || hasHoh2LastName;

  const isSecondHohRequired = hasAnyHoh2Name;

  const isExperiencingHomelessness = Array.isArray(hoh1ServiceReasons)
    ? hoh1ServiceReasons.some((v) => isHomelessnessCode(v))
    : false;

  const calWorksProgramActivityCode = findCodeByDescription(
    reasonForServiceList,
    CALWORKS_PROGRAM_ACTIVITY_DESCRIPTION
  );

  const allowedCalWorksCodes = new Set(
    [
      findCodeByDescription(calWORKsList, CALWORKS_CASH_AID_DESCRIPTION),
      findCodeByDescription(
        calWORKsList,
        CALWORKS_DIVERSION_SERVICES_DESCRIPTION
      ),
    ].filter((code): code is string => code !== undefined)
  );

  useEffect(() => {
    if (reasonForServiceList.length > 0 && !calWorksProgramActivityCode) {
      console.warn(
        `AB1500 CalWORKs validation: no Reason For Service option matches "${CALWORKS_PROGRAM_ACTIVITY_DESCRIPTION}" in the reference data — this rule will never trigger until the description text is corrected.`
      );
    }
  }, [reasonForServiceList, calWorksProgramActivityCode]);

  useEffect(() => {
    if (calWORKsList.length === 0) return;

    const hasAllowedCalWorksCode =
      findCodeByDescription(calWORKsList, CALWORKS_CASH_AID_DESCRIPTION) !==
        undefined ||
      findCodeByDescription(
        calWORKsList,
        CALWORKS_DIVERSION_SERVICES_DESCRIPTION
      ) !== undefined;

    if (!hasAllowedCalWorksCode) {
      console.warn(
        `AB1500 CalWORKs validation: neither "${CALWORKS_CASH_AID_DESCRIPTION}" nor "${CALWORKS_DIVERSION_SERVICES_DESCRIPTION}" matches a CalWORKs option in the reference data — this rule would reject every CalWORKs Recipient value whenever it triggers.`
      );
    }
  }, [calWORKsList]);

  const requiresCalWorksCashAidOrDiversion = Boolean(
    calWorksProgramActivityCode &&
      ((Array.isArray(hoh1ServiceReasons) &&
        hoh1ServiceReasons.some(
          (v) => String(v ?? "").trim() === calWorksProgramActivityCode
        )) ||
        (Array.isArray(hoh2ServiceReasons) &&
          hoh2ServiceReasons.some(
            (v) => String(v ?? "").trim() === calWorksProgramActivityCode
          )))
  );

  const validateCalWorksRecipient = (
    value: string | undefined
  ): string | true => {
    if (!requiresCalWorksCashAidOrDiversion) return true;

    return allowedCalWorksCodes.has(String(value ?? "").trim())
      ? true
      : CALWORKS_RECIPIENT_INVALID_MESSAGE;
  };

  useEffect(() => {
    if (!isExperiencingHomelessness) return;

    const fields: (keyof FAMI)[] = [
      "ResidentialAddress1",
      "ResidentialAddress2",
      "ResidentialCity",
      "ResidentialState",
      "ResidentialZip",
    ];

    clearErrors(fields);
  }, [isExperiencingHomelessness, clearErrors]);

  const requireResidentialAddress1 = !isExperiencingHomelessness;
  const requireResidentialCityStateZip = !isExperiencingHomelessness;

  const requireMailingCityStateZip = Boolean(mailingAddress1?.trim());

  const handleMailingToggle = (checked: boolean) => {
    setShowMailingAddress(checked);

    if (!checked) {
      const fields: (keyof FAMI)[] = [
        "MailingAddress1",
        "MailingAddress2",
        "MailingCity",
        "MailingState",
        "MailingZip",
      ];

      fields.forEach((name) => {
        setValue(name, "", {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        });
      });

      clearErrors(fields);
    }
  };

  const getServiceReasons = (prefix: HohPrefix) => {
    return serviceReasonFieldNames(prefix).map((field) => {
      const current = getValues(field as any);
      const fallback = family[field];
      return String(current ?? fallback ?? "").trim();
    });
  };

  const setServiceReasons = (prefix: HohPrefix, values: string[]) => {
    serviceReasonFieldNames(prefix).forEach((field, i) => {
      setValue(field as any, values[i] ?? "", { shouldDirty: true });
    });
  };

  const hasAnyServiceReasonSelected = (prefix: HohPrefix) => {
    return serviceReasonFieldNames(prefix).some((field) => {
      const v = getValues(field as any);
      return Boolean(String(v ?? "").trim());
    });
  };

  const validateEffectiveStartDate = (
    value: string | undefined
  ): string | true => {
    return validateEffectiveStartDateForProgramYear(
      value,
      getValues("ProgramYear")
    );
  };

  return {
    showMailingAddress,
    handleMailingToggle,
    isSecondHohRequired,
    requireResidentialAddress1,
    requireResidentialCityStateZip,
    requireMailingCityStateZip,
    isExperiencingHomelessness,
    getServiceReasons,
    setServiceReasons,
    hasAnyServiceReasonSelected,
    validateEffectiveStartDate,
    validateCalWorksRecipient,
  };
}
